import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMerchants as appGetMerchants } from "../../store/app/action.js";
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton
} from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import FileUpload from "../../components/FileUpload.jsx";
import Loader from "../../components/Loader.jsx";
import MarketplaceAPIDialog from "./MarketplaceAPIDialog.jsx";
import {
  getMarketplaceOnboardingStatus,
  getMarketplaceLegalAgreement,
  acceptMarketplaceLegalAgreement,
  uploadMarketplaceOnboardingMenu,
  getPOSIntegrations,
  MARKETPLACE_LICENSE_TYPES,
} from "../../api/marketplace.js";
import useLicenseDocuments from "../../hooks/useLicenseDocuments.js";
import LicenseTypeCard from "../../partials/licenses/LicenseTypeCard.jsx";
import {
  LICENSE_STORAGE_NOTICE,
  buildOptOutAnswers,
  isLicenseTypeSatisfied,
} from "../../partials/licenses/licenseUtils.js";

const STEPS = [
  { key: "legal", label: "Legal Documents" },
  { key: "stripe", label: "Stripe Connect" },
  { key: "licenses", label: "Licenses" },
  { key: "menu", label: "Menu Upload" },
];

function stepIndex(stepKey) {
  const i = STEPS.findIndex((s) => s.key === stepKey);
  return i === -1 ? 0 : i;
}

function ProgressBar({ currentIndex }) {
  const pct = Math.min(100, Math.round(((currentIndex) / STEPS.length) * 100));
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Step {Math.min(currentIndex + 1, STEPS.length)} of {STEPS.length}
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {STEPS[Math.min(currentIndex, STEPS.length - 1)].label}
        </p>
      </div>
      <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-300 rounded-full bg-theme-button-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function LegalStep({ onAdvance, onError }) {
  const [signerEmail, setSignerEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMarketplaceLegalAgreement();
        console.log(`[LegalStep] res:`, JSON.stringify(res));
        if (cancelled) return;
        setSignerEmail(res?.signer_email || null);
      } catch (e) {
        if (!cancelled) {
          onError(
            e?.response?.data?.message || e.message || "Failed to load agreement"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await acceptMarketplaceLegalAgreement();
      // BE returns the fresh stripe account link in the accept response.
      // Hand it up so StripeStep has a URL immediately, before the
      // merchant refresh hydrates selectedLocation.stripe_account_link.
      const stripeUrl =
        res?.stripe_account_link_url ?? res?.stripe_account_link?.url ?? null;
      onAdvance({ stripeUrl });
    } catch (e) {
      onError(
        e?.response?.data?.message || e.message || "Failed to mark agreement as signed"
      );
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
        Legal Documents
      </h2>

      {signerEmail ? (
        <div className="rounded border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4 text-sm text-slate-700 dark:text-slate-200">
          An agreement was sent to <span className="font-medium">{signerEmail}</span>,
          please complete the document before continuing.
        </div>
      ) : (
        <div className="text-sm text-amber-600 dark:text-amber-400">
          No agreement has been sent yet. Contact your Saucey rep if this persists.
        </div>
      )}

      <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Only click below after you&apos;ve completed and signed the agreement.
        </p>
        <SecondaryButton
          onClick={handleAccept}
          disabled={!signerEmail || accepting}
          loading={accepting}
        >
          I&apos;ve signed it
        </SecondaryButton>
      </div>
    </div>
  );
}

function StripeStep({ stripeAccountLinkUrl }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
        Stripe Connect
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Finish setting up your Stripe account so we can pay you out. You&apos;ll
        provide your business details, banking info, and verify your identity.
        Come back here once you&apos;re done — we&apos;ll advance automatically.
      </p>

      {stripeAccountLinkUrl ? (
        <a
          href={stripeAccountLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex"
        >
          <PrimaryButton>Finish Stripe Setup</PrimaryButton>
        </a>
      ) : (
        <div className="text-sm text-amber-600 dark:text-amber-400">
          No Stripe setup link is available yet. Contact your Saucey rep if
          this persists.
        </div>
      )}
    </div>
  );
}

function LicensesStep({ onComplete, onError }) {
  const { docsByType, loading, uploadingType, uploadError, setUploadError, upload } =
    useLicenseDocuments({ onLoadError: onError });
  // naFlags: type -> boolean, only meaningful for optOut categories.
  const [naFlags, setNaFlags] = useState({});
  const [continuing, setContinuing] = useState(false);

  const canContinue = MARKETPLACE_LICENSE_TYPES.every((t) =>
    isLicenseTypeSatisfied(t, docsByType, naFlags)
  );

  const handleContinue = async () => {
    setContinuing(true);
    setUploadError(null);
    try {
      await onComplete(buildOptOutAnswers(naFlags));
    } catch (e) {
      // Backend 400 (e.g. "missing required document(s): business_license, w9").
      // Surface it and keep the seller on this screen.
      setUploadError(
        e?.response?.data?.message ||
          e.message ||
          "Some required documents are still missing."
      );
    } finally {
      setContinuing(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
        Licenses
      </h2>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {LICENSE_STORAGE_NOTICE}
      </p>

      {MARKETPLACE_LICENSE_TYPES.map((t) => (
        <LicenseTypeCard
          key={t.type}
          licenseType={t}
          docs={docsByType[t.type]}
          uploading={uploadingType === t.type}
          onFile={(file) => upload(t.type, file)}
          isNA={naFlags[t.type] === true}
          onNaChange={(checked) =>
            setNaFlags((prev) => ({ ...prev, [t.type]: checked }))
          }
        />
      ))}

      {uploadError && (
        <div className="text-sm text-red-500">{uploadError}</div>
      )}

      <div className="flex justify-end pt-2 border-t border-slate-200 dark:border-slate-700">
        <PrimaryButton
          onClick={handleContinue}
          disabled={!canContinue || continuing}
          loading={continuing}
        >
          Continue to Menu Upload
        </PrimaryButton>
      </div>
    </div>
  );
}

function MenuStep({ seller, onComplete, onError }) {
  const [posSystem, setPosSystem] = useState("");
  const [otherPosSystem, setOtherPosSystem] = useState("");
  const [posError, setPosError] = useState(null);
  const [file, setFile] = useState(null);
  const [initialFiles, setInitialFiles] = useState(undefined);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [posIntegrations, setPOSIntegrations] = useState([]);
  const handleTestResults = (csvFile) => {
    setFile(csvFile);
    setInitialFiles([csvFile]);
    // Remount so Dropzone picks up the new initialFiles cleanly
    setFileUploadKey((k) => k + 1);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let res = await getPOSIntegrations();
      if (cancelled) return;
      let other = {
        _id: "000000000000000000000000",
        name: "Other",
        description: "Other POS system not listed",
      }
      setPOSIntegrations(res.concat(other));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async () => {
    if (!file) return;
    const pos = posSystem === "Other" ? otherPosSystem.trim() : posSystem;
    if (!pos) {
      // Shown inline under the dropdown — onError would replace the whole wizard
      setPosError(
        posSystem === "Other"
          ? "Please enter your POS system"
          : "Please select a POS integration"
      );
      return;
    }
    setSubmitting(true);
    try {
      await uploadMarketplaceOnboardingMenu(seller?.store_id, file, pos);
      onComplete();
    } catch (e) {
      onError(
        e?.response?.data?.message || e.message || "Menu upload failed",
        "menu-upload"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
        Menu Upload
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Configure your API credentials below, then upload your product catalog
        CSV so we can populate your store.
      </p>
      <MarketplaceAPIDialog
        seller={seller}
        onComplete={onComplete}
        onError={onError}
        onTestResults={handleTestResults}
      />
      {/* POS Integrations Dropdown */}
      <div className="flex flex-col gap-2">
        <label htmlFor="pos-integration" className="text-sm text-slate-600 dark:text-slate-300">
          Point of Sale System (POS)
        </label>
        <select
          id="pos-integration"
          className="w-full p-2 rounded-md border border-slate-300 dark:border-slate-700"
          value={posSystem}
          onChange={(e) => {
            setPosSystem(e.target.value);
            setPosError(null);
          }}
        >
          <option value="" disabled>Select a POS…</option>
          {posIntegrations.map((integration) => (
            <option key={integration._id} value={integration.name}>
              {integration.name}
            </option>
          ))}
        </select>
        {posSystem === "Other" && (
          <input
            type="text"
            id="other-pos-system"
            placeholder="Enter your POS system"
            className="w-full p-2 rounded-md border border-slate-300 dark:border-slate-700"
            value={otherPosSystem}
            onChange={(e) => {
              setOtherPosSystem(e.target.value);
              setPosError(null);
            }}
          />
        )}
        {posError && <div className="text-sm text-red-500">{posError}</div>}
      </div>
      <FileUpload
        key={fileUploadKey}
        accept=".csv,text/csv"
        initialFiles={initialFiles}
        onChange={(files) => setFile(files?.[0] || null)}
        inputContent="Drop your menu CSV or click to browse"
      />
      <div className="flex justify-end gap-3">
        <PrimaryButton
          onClick={handleSubmit}
          disabled={!file || submitting}
          loading={submitting}
        >
          Submit menu
        </PrimaryButton>
      </div>
    </div>
  );
}

function UnderReviewPanel({ email }) {
  return (
    <div className="text-center py-12 space-y-4">
      <div className="text-5xl">🕐</div>
      <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
        Your store is under review
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto">
        Thanks for completing onboarding. We&apos;re reviewing your submission
        and will email <span className="font-medium">{email}</span> as soon as
        you&apos;re approved and live on Saucey Marketplace.
      </p>
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto pt-4">
        {STEPS.map((s) => (
          <div
            key={s.key}
            className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs"
          >
            <div className="font-semibold text-emerald-700 dark:text-emerald-300">
              ✓ {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LivePanel({ email }) {
  return (
    <div className="text-center py-12 space-y-4">
      <div className="text-5xl">🎉</div>
      <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
        You&apos;re live
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto">
        Your store has been approved. A confirmation has been sent to{" "}
        <span className="font-medium">{email}</span>. Welcome to Saucey
        Marketplace.
      </p>
    </div>
  );
}

function MarketplaceOnboarding() {
  const { user, selectedLocation } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Captured from the /legal/accept response so StripeStep has a URL the
  // instant the seller advances, before selectedLocation rehydrates.
  const [pendingStripeUrl, setPendingStripeUrl] = useState(null);

  const stripeAccountLinkUrl =
    selectedLocation?.stripe_account_link?.url ?? pendingStripeUrl;

  const refresh = async ({ stripeUrl } = {}) => {
    if (stripeUrl) {
      setPendingStripeUrl(stripeUrl);
    }
    try {
      // Re-pull the merchant record so selectedLocation reflects fields the BE
      // mutated during the last step (e.g. stripe_account_link after accept).
      dispatch(appGetMerchants);
      const res = await getMarketplaceOnboardingStatus();
      setStatus(res);
    } catch (e) {
      setError(
        e?.response?.data?.message || e.message || "Could not load onboarding status"
      );
    } finally {
      setLoading(false);
    }
  };

  // Licenses "continue" hits the status completion gate with the opt-out answers.
  // On a validation 400 (missing required docs) this throws so the licenses
  // screen can surface the message inline — it must NOT flip the global error
  // state, which would replace the whole wizard.
  const completeLicenses = async (licenseAnswers) => {
    dispatch(appGetMerchants);
    const res = await getMarketplaceOnboardingStatus(licenseAnswers);
    setStatus(res);
  };

  useEffect(() => {
    refresh();
  }, []);

  const currentIndex = useMemo(() => {
    if (!status) return 0;
    if (!status.onboarding) return STEPS.length;
    if (status.onboarding_step === "under_review") return STEPS.length;
    return stepIndex(status.onboarding_step || "legal");
  }, [status]);

  const isLive = status && status.onboarding === false;
  const isUnderReview =
    status && status.onboarding === true && status.onboarding_step === "under_review";

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12 w-full max-w-3xl mx-auto">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12 w-full max-w-3xl mx-auto">
        <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
        <div className="mt-4">
          <SecondaryButton onClick={refresh}>Retry</SecondaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-3xl mx-auto">
      {!isUnderReview && (
        <>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
            Welcome to Saucey Marketplace
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
            Hi {user?.name || "there"} — let&apos;s finish setting up your store.
            Each step unlocks the next.
          </p>
        </>
      )}

      {!isLive && !isUnderReview && <ProgressBar currentIndex={currentIndex} />}

      {isLive ? (
        <LivePanel email={user?.email} />
      ) : isUnderReview ? (
        <UnderReviewPanel email={user?.email} />
      ) : currentIndex === 0 ? (
        <LegalStep onAdvance={refresh} onError={setError} />
      ) : currentIndex === 1 ? (
        <StripeStep stripeAccountLinkUrl={stripeAccountLinkUrl} />
      ) : currentIndex === 2 ? (
        <LicensesStep onComplete={completeLicenses} onError={setError} />
      ) : (
        <MenuStep
          seller={selectedLocation}
          onComplete={refresh}
          onError={setError}
        />
      )}
    </div>
  );
}

export default MarketplaceOnboarding;
